import React, { useState, useEffect } from "react";
import { FiChevronDown, FiChevronRight, FiPlus, FiEdit2, FiTrash2, FiX, FiList, FiGitBranch } from "react-icons/fi";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const SiteChartNode = ({ node, onAdd, onEdit, onDelete }) => {
  return (
    <div className="flex flex-col items-center">
      <div className="group relative bg-white dark:bg-gray-800 border-2 border-slate-200 dark:border-slate-700 w-56 rounded-xl shadow-sm p-4 transition hover:border-blue-400 dark:hover:border-blue-500 mt-2 mb-2 z-10 hover:shadow-md">
        <h3 className="font-bold text-gray-800 dark:text-gray-100 text-base truncate mb-1">{node.name}</h3>
        <span className="inline-block px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold uppercase tracking-wide rounded-full mb-3">
          {node.type || 'Facility'}
        </span>
        <div className="text-left text-xs space-y-1.5 bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-700/50">
          <div className="flex justify-between items-center"><span className="text-slate-500">Scope 1</span> <span className="font-medium text-orange-600 dark:text-orange-400">{Number(node.scope[0] || 0).toFixed(2)}</span></div>
          <div className="flex justify-between items-center"><span className="text-slate-500">Scope 2</span> <span className="font-medium text-blue-600 dark:text-blue-400">{Number(node.scope[1] || 0).toFixed(2)}</span></div>
          <div className="flex justify-between items-center"><span className="text-slate-500">Scope 3</span> <span className="font-medium text-green-600 dark:text-green-400">{Number(node.scope[2] || 0).toFixed(2)}</span></div>
          <div className="border-t border-slate-200 dark:border-slate-700 mt-1.5 pt-1.5 flex justify-between items-center"><span className="font-semibold text-slate-700 dark:text-slate-300">Total Eq</span> <span className="font-bold text-sm text-gray-900 dark:text-white">{Number(node.total || 0).toFixed(2)}</span></div>
        </div>

        <div className="absolute -top-3 -right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1.5 z-20">
          <button onClick={() => onAdd(node.id)} className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-blue-600" title="Add Sub-Site"><FiPlus size={16}/></button>
          <button onClick={() => onEdit(node)} className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-amber-600" title="Edit Site"><FiEdit2 size={16}/></button>
          <button onClick={() => onDelete(node.id)} className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-red-600" title="Delete Site"><FiTrash2 size={16}/></button>
        </div>
      </div>

      {node.children && node.children.length > 0 && (
        <div className="flex flex-col items-center">
          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
          
          <div className="flex flex-row justify-center relative">
            {node.children.map((child, index) => {
              const isFirst = index === 0;
              const isLast = index === node.children.length - 1;
              const isOnly = node.children.length === 1;

              return (
                <div key={child.id} className="relative flex flex-col items-center px-4">
                  {!isOnly && (
                    <div className={`absolute top-0 h-px bg-slate-300 dark:bg-slate-600 -z-10
                          ${isFirst ? 'left-1/2 right-0' : isLast ? 'left-0 right-1/2' : 'left-0 right-0'}`}
                    ></div>
                  )}
                  <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 -z-10"></div>
                  
                  <SiteChartNode node={child} onAdd={onAdd} onEdit={onEdit} onDelete={onDelete} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TreeTableRow = ({ node, level, isExpanded, onToggle, onAdd, onEdit, onDelete }) => {
  const hasChildren = node.children && node.children.length > 0;

  return (
    <tr className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition">
      <td className="p-3">
        <div
          className="flex items-center"
          style={{ paddingLeft: `${level * 2}rem` }}
        >
          {hasChildren ? (
            <button
              onClick={() => onToggle(node.id)}
              className="mr-2 text-gray-500 hover:text-blue-500 rounded p-1"
            >
              {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
            </button>
          ) : (
            <span className="w-6 mr-2 inline-block"></span>
          )}
          <span className="font-medium text-gray-800 dark:text-gray-200">
            {node.name}
          </span>
        </div>
      </td>
      <td className="p-3 text-gray-600 dark:text-gray-400">{node.type}</td>
      <td className="p-3 font-semibold text-gray-800 dark:text-gray-200">
        {node.total.toFixed(2)}
      </td>
      <td className="p-3 text-orange-500">{node.scope[0].toFixed(2)}</td>
      <td className="p-3 text-blue-500">{node.scope[1].toFixed(2)}</td>
      <td className="p-3 text-green-500">{node.scope[2].toFixed(2)}</td>
      <td className="p-3 flex items-center space-x-2">
        <button onClick={() => onAdd(node.id)} className="text-blue-500 hover:text-blue-700 mx-1" title="Add Sub-Site">
          <FiPlus />
        </button>
        <button onClick={() => onEdit(node)} className="text-gray-500 hover:text-gray-700 mx-1" title="Edit Site">
          <FiEdit2 />
        </button>
        <button onClick={() => onDelete(node.id)} className="text-red-500 hover:text-red-700 mx-1" title="Delete Site">
          <FiTrash2 />
        </button>
      </td>
    </tr>
  );
};

export default function HierarchyTreeTable() {
  const [treeData, setTreeData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [viewMode, setViewMode] = useState("table"); // "table" or "tree"
  const axiosPrivate = useAxiosPrivate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeParentId, setActiveParentId] = useState(null);
  const [activeEditId, setActiveEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Facility'
  });

  const fetchSites = async () => {
    try {
      const response = await axiosPrivate.get('/sites');
      const flatData = response.data;

      const buildTree = (sites, parentId = null) => {
        return sites
          .filter(site => site.parentSiteId === parentId)
          .map(site => {
            const children = buildTree(sites, site.id);
            const childTotal = children.reduce((sum, child) => sum + (child.total || 0), 0);
            const childScope0 = children.reduce((sum, child) => sum + (child.scope?.[0] || 0), 0);
            const childScope1 = children.reduce((sum, child) => sum + (child.scope?.[1] || 0), 0);
            const childScope2 = children.reduce((sum, child) => sum + (child.scope?.[2] || 0), 0);

            return {
              ...site,
              total: (site.total || 0) + childTotal,
              scope: [
                (site.scope?.[0] || 0) + childScope0,
                (site.scope?.[1] || 0) + childScope1,
                (site.scope?.[2] || 0) + childScope2,
              ],
              children
            };
          });
      };

      const hierarchalData = buildTree(flatData);
      setTreeData(hierarchalData);

      if (expandedRows.size === 0 && hierarchalData.length > 0) {
        const topLevelIds = hierarchalData.map(node => node.id);
        setExpandedRows(new Set(topLevelIds));
      }
    } catch (error) {
      console.error("Error fetching hierarchy:", error);
    }
  };

  useEffect(() => {
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleAddChild = (parentId) => {
    setActiveParentId(parentId);
    setActiveEditId(null);
    setFormData({
      name: '',
      type: parentId ? 'Facility' : 'HQ'
    });
    setIsModalOpen(true);
  };

  const handleEdit = (node) => {
    setActiveEditId(node.id);
    setActiveParentId(node.parentSiteId);
    setFormData({
      name: node.name,
      type: node.type || 'Facility'
    });
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (activeEditId) {
        await axiosPrivate.put(`/sites/${activeEditId}`, {
          name: formData.name,
          type: formData.type
        });
      } else {
        await axiosPrivate.post('/sites', {
          name: formData.name,
          parentSiteId: activeParentId,
          type: formData.type
        });
        if (activeParentId) {
          setExpandedRows(prev => new Set(prev).add(activeParentId));
        }
      }
      fetchSites();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving site:", error);
      alert(error.response?.data?.error || "Failed to save site");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this site and all its sub-sites?")) {
      try {
        await axiosPrivate.delete(`/sites/${id}`);
        fetchSites();
      } catch (error) {
        console.error("Error deleting site:", error);
        alert(error.response?.data?.error || "Failed to delete site");
      }
    }
  };

  const renderTreeRows = (nodes, level = 0) => {
    let rows = [];
    nodes.forEach(node => {
      const isExpanded = expandedRows.has(node.id);
      rows.push(
        <TreeTableRow
          key={node.id}
          node={node}
          level={level}
          isExpanded={isExpanded}
          onToggle={toggleRow}
          onAdd={handleAddChild}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      );
      if (isExpanded && node.children && node.children.length > 0) {
        rows = rows.concat(renderTreeRows(node.children, level + 1));
      }
    });
    return rows;
  };

  return (
    <div className="flex flex-col p-4 md:p-8 w-full max-w-full mx-auto overflow-auto h-full space-y-8 bg-gray-50/50 dark:bg-gray-900/50">
      <div className="flex items-center justify-between flex-wrap gap-4 sticky left-0 right-0">
        <div>
          <h2 className="text-2xl font-bold dark:text-white">
            Company Site Hierarchy
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Display Mode: <span className="font-semibold text-blue-600 dark:text-blue-400">{viewMode === 'table' ? 'Table Mode' : 'Tree Chart Mode'}</span>
          </p>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          {/* View Mode Switcher */}
          <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg text-xs font-medium">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 font-bold shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
              }`}
            >
              <FiList size={14} /> Table Mode
            </button>
            <button
              onClick={() => setViewMode('tree')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition ${
                viewMode === 'tree'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 font-bold shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
              }`}
            >
              <FiGitBranch size={14} /> Tree Mode
            </button>
          </div>

          <button
            onClick={() => handleAddChild(null)}
            className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 shadow-md transition text-sm"
          >
            + Add Top-Level Site
          </button>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="bg-white dark:bg-darkbg4 rounded-xl shadow border dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider">
                  <th className="p-4 font-semibold">Site Name</th>
                  <th className="p-4 font-semibold">Type</th>
                  <th className="p-4 font-semibold">Total Emission</th>
                  <th className="p-4 font-semibold">Scope 1</th>
                  <th className="p-4 font-semibold">Scope 2</th>
                  <th className="p-4 font-semibold">Scope 3</th>
                  <th className="p-4 font-semibold w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {treeData.length > 0 ? (
                  renderTreeRows(treeData)
                ) : (
                  <tr>
                    <td colSpan="7" className="p-6 text-center text-gray-500">
                      No sites found. Add a top-level site to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="min-w-min flex justify-center py-10 px-4 bg-white dark:bg-darkbg4 border dark:border-gray-700 rounded-xl shadow-inner overflow-x-auto">
          <div className="flex gap-16 items-start">
            {treeData.length > 0 ? (
              treeData.map(rootNode => (
                <SiteChartNode
                  key={rootNode.id}
                  node={rootNode}
                  onAdd={handleAddChild}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))
            ) : (
              <div className="text-gray-500 py-10 text-center">
                No sites found. Add a top-level site to get started.
              </div>
            )}
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-gray-700 pb-3">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {activeEditId ? 'Edit Site' : activeParentId ? 'Add Sub-Site' : 'Add Top-Level Site'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700">
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleModalSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Site Name</label>
                <input
                  required
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                  placeholder="e.g. Factory A"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                >
                  <option value="HQ">HQ</option>
                  <option value="Office">Office</option>
                  <option value="Factory">Factory</option>
                  <option value="Branch">Branch</option>
                  <option value="Warehouse">Warehouse</option>
                  <option value="Facility">Facility</option>
                </select>
              </div>
              <div className="pt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 shadow-md transition"
                >
                  Save Site
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
